import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form-latest';
import { ErrorMessage } from '@hookform/error-message';

import { _isNotEmpty, _notNil } from '@/littledash';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Button from '@/components/UI/Button';
import SearchSelect from '@/components/UI/SearchSelect';

import useCredentials from '@/components/Team/ApiKeys/useCredentials';

interface Scope {
  name: string;
  value: string;
}

interface AddApiKeyProps {
  closeModal: () => void;
  handleCallback: (clientId: string, clientSecret: string) => void;
  scopes: ReadonlyArray<Scope>;
  testId?: string;
}

interface AddApiKeyFormData {
  name: string;
  scopes: Array<Scope>;
}

const AddApiKey: FC<AddApiKeyProps> = ({ closeModal, handleCallback, scopes, testId = 'add-api-key' }) => {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<Error | null>(null);

  const { addApiKey } = useCredentials();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AddApiKeyFormData>({
    defaultValues: {
      name: '',
      scopes: [],
    },
  });

  const onSubmit = (data: AddApiKeyFormData) => {
    setLoading(true);
    return addApiKey(data)
      .then((res) => {
        closeModal();
        const clientId = res.body?.client_id ?? '';
        const clientSecret = res.body?.client_secret ?? '';
        handleCallback(clientId, clientSecret);
      })
      .catch((error: Error) => {
        setLoading(false);
        setApiError(error);
      });
  };

  const sections = useMemo(() => [{ items: scopes }], [scopes]);

  return (
    <div className="center mw6 ui-card" data-test-component="AddApiKey" data-testid={`${testId}__modal`}>
      <div className="pa3 bg-white" data-test-element="header">
        <h2 className="normal lh-title f4" data-testid={`${testId}__title`}>
          Create an API key
        </h2>
        {_notNil(apiError) && <ApiErrorBanner error={apiError} />}
      </div>
      <form onSubmit={handleSubmit(onSubmit)} data-test-element="form">
        <div className="pa3">
          <div className="w-100 db">
            <label htmlFor="name" data-testid={`${testId}__name-label`}>
              Name of API key
            </label>
            <div className="mb3">
              <input
                type="text"
                id="name"
                data-testid={`${testId}__name-input`}
                className={`${_notNil(errors.name) ? 'input__error' : ''} mb0`}
                {...register('name', {
                  required: 'Required',
                })}
              />
              <ErrorMessage
                errors={errors}
                name="name"
                render={({ message }) => (
                  <p className="f6 red db lh-copy pt1" data-testid={`${testId}__name-error`}>
                    {message}
                  </p>
                )}
              />
            </div>

            <label htmlFor="scopes" data-testid={`${testId}__scopes-label`}>
              Scopes
            </label>
            <div className="ui-card" data-test-element="scopes-section">
              <Controller
                name="scopes"
                control={control}
                rules={{
                  validate: (data) => {
                    if (!_isNotEmpty(data)) {
                      return 'You must select at least one scope';
                    }
                    return true;
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <SearchSelect
                    className={_notNil(errors.scopes) ? 'search_select__error' : ''}
                    sections={sections}
                    selected={value}
                    setSelected={onChange}
                    sort
                    sortAccessor={'name'}
                    testPrefix={`${testId}-scopes`}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="scopes"
                render={({ message }) => (
                  <p className="f6 red db lh-copy pt1" data-testid={`${testId}__scopes-error`}>
                    {message}
                  </p>
                )}
              />
            </div>
          </div>
        </div>
        <div className="pa3 mt3 bt b--moon-gray bg-white" data-test-element="footer">
          <Button submit className="mr2" disabled={loading} testId={`${testId}__submit`}>
            {loading ? 'Creating...' : 'Create API key'}
          </Button>
          <Button plain onClick={closeModal} testId={`${testId}__cancel`}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddApiKey;
